import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { getApiError } from '../../lib/utils';
import CustomerLayout from '../../components/layouts/CustomerLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { 
  MapPin, 
  Send, 
  Package, 
  Plus, 
  Trash2,
  Info,
  AlertCircle,
  Shield,
  Clock,
  Headphones,
  FileText,
  Settings,
  Pencil
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const packageTypes = [
  { value: 'pallet', label: 'Pallet' },
  { value: 'box', label: 'Box' },
  { value: 'crate', label: 'Crate' },
  { value: 'flyer', label: 'Flyer' },
  { value: 'other', label: 'Other' },
];

const southAfricanCities = [
  'Johannesburg', 'Cape Town', 'Durban', 'Pretoria', 'Port Elizabeth', 
  'Gqeberha', 'Bloemfontein', 'East London', 'Polokwane', 'Nelspruit',
  'Kimberley', 'Rustenburg', 'Richards Bay', 'Pietermaritzburg', 'George',
  'Lanseria', 'Durban Port', 'Pretoria Central', 'Midrand'
];

const CreateWaybill = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [reference, setReference] = useState('');
  const [deliveryTypes, setDeliveryTypes] = useState([]);
  const [selectedDeliveryType, setSelectedDeliveryType] = useState(null);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState('');
  const [addressModalOpen, setAddressModalOpen] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [addressForm, setAddressForm] = useState({ label: '', company_name: '', address: '', city: '', contact_person: '', contact_number: '', email: '' });
  const [showSaveInline, setShowSaveInline] = useState(false);
  const [saveLabel, setSaveLabel] = useState('');

  const getExpectedDeliveryDate = (dt) => {
    if (!dt || dt.days == null || dt.days < 0) return null;
    
    if (dt.days === 0) return new Date(); // Same day
    
    // Add calendar days, then bump to Monday if it lands on a weekend
    const date = new Date();
    date.setDate(date.getDate() + dt.days);
    const day = date.getDay();
    if (day === 0) date.setDate(date.getDate() + 1); // Sunday -> Monday
    if (day === 6) date.setDate(date.getDate() + 2); // Saturday -> Monday
    return date;
  };

  const formatDate = (date) => {
    if (!date) return '';
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    return `${days[date.getDay()]}, ${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
  };
  
  const [formData, setFormData] = useState({
    sender_company: '',
    sender_address: '',
    sender_contact_person: '',
    sender_contact_number: '',
    sender_email: '',
    receiver_company: '',
    receiver_address: '',
    receiver_contact_person: '',
    receiver_contact_number: '',
    receiver_email: '',
    origin_city: '',
    destination_city: '',
    customer_notes: '',
  });

  const [items, setItems] = useState([
    { _key: 'item_0', package_type: 'pallet', description: '', quantity: 1, weight: 0, length: 0, width: 0, height: 0, declared_value: '', special_handling: '', item_reference: '' }
  ]);
  const itemKeyRef = React.useRef(1);

  // Generate reference on mount + fetch delivery types + auto-populate sender
  const [refGenerated, setRefGenerated] = React.useState(false);
  React.useEffect(() => {
    axios.get(`${API_URL}/api/delivery-types`).then(res => {
      setDeliveryTypes(res.data.delivery_types);
    }).catch(() => {});
    axios.get(`${API_URL}/api/addresses`).then(res => {
      setSavedAddresses(res.data.addresses || []);
    }).catch(() => {});
  }, []);

  React.useEffect(() => {
    if (user) {
      if (!refGenerated) {
        axios.get(`${API_URL}/api/waybills/next-reference`).then(res => {
          setReference(res.data.reference);
        }).catch(() => {
          const prefix = (user.prefix || 'WB').toUpperCase();
          setReference(`${prefix}00001-OL`);
        });
        setRefGenerated(true);
      }
      setFormData(prev => ({
        ...prev,
        sender_company: user.company_name || '',
        sender_address: user.company_address || '',
        sender_contact_person: user.full_name || '',
        sender_contact_number: user.company_phone || user.phone || '',
        sender_email: user.company_email || user.email || '',
        origin_city: user.company_city || '',
      }));
    }
  }, [user, refGenerated]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleCityChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    setItems(newItems);
  };

  const addItem = () => {
    const key = `item_${itemKeyRef.current++}`;
    setItems([...items, { _key: key, package_type: 'box', description: '', quantity: 1, weight: 0, length: 0, width: 0, height: 0, declared_value: '', special_handling: '', item_reference: '' }]);
  };

  const removeItem = (index) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  const handleSubmit = async (submitType = 'quote') => {
    setLoading(true);

    try {
      const payload = {
        ...formData,
        submit_type: submitType,
        delivery_type_code: selectedDeliveryType?.code || null,
        items: items.map(item => ({
          ...item,
          quantity: parseInt(item.quantity) || 1,
          weight: parseFloat(item.weight) || 0,
          length: parseFloat(item.length) || 0,
          width: parseFloat(item.width) || 0,
          height: parseFloat(item.height) || 0,
          declared_value: item.declared_value ? parseFloat(item.declared_value) : null,
          item_reference: item.item_reference || null,
        }))
      };

      const response = await axios.post(`${API_URL}/api/waybills`, payload);
      toast.success(submitType === 'direct' ? 'Waybill confirmed and sent!' : 'Quote request submitted!');
      navigate(`/waybill/${response.data.waybill_id}`);
    } catch (err) {
      console.error('Error creating waybill:', err);
      toast.error(getApiError(err, 'Failed to create waybill'));
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAddress = (addressId) => {
    setSelectedAddressId(addressId);
    if (!addressId) return;
    const addr = savedAddresses.find(a => a.address_id === addressId);
    if (addr) {
      setFormData(prev => ({
        ...prev,
        receiver_company: addr.company_name || '',
        receiver_address: addr.address || '',
        receiver_contact_person: addr.contact_person || '',
        receiver_contact_number: addr.contact_number || '',
        receiver_email: addr.email || '',
        destination_city: addr.city || '',
      }));
    }
  };

  const openAddressModal = (addr = null) => {
    if (addr) {
      setEditingAddress(addr);
      setAddressForm({
        label: addr.label || '',
        company_name: addr.company_name || '',
        address: addr.address || '',
        city: addr.city || '',
        contact_person: addr.contact_person || '',
        contact_number: addr.contact_number || '',
        email: addr.email || '',
      });
      setShowAddressForm(true);
    } else {
      setEditingAddress(null);
      setAddressForm({ label: '', company_name: '', address: '', city: '', contact_person: '', contact_number: '', email: '' });
      setShowAddressForm(false);
    }
    setAddressModalOpen(true);
  };

  const handleSaveAddress = async () => {
    if (!addressForm.company_name) { toast.error('Company name is required'); return; }
    try {
      if (editingAddress) {
        await axios.delete(`${API_URL}/api/addresses/${editingAddress.address_id}`);
      }
      await axios.post(`${API_URL}/api/addresses`, {
        ...addressForm,
        label: addressForm.label || addressForm.company_name
      });
      const refreshed = await axios.get(`${API_URL}/api/addresses`);
      setSavedAddresses(refreshed.data.addresses || []);
      toast.success(editingAddress ? 'Address updated' : 'Address saved');
      setShowAddressForm(false);
      setEditingAddress(null);
      setAddressForm({ label: '', company_name: '', address: '', city: '', contact_person: '', contact_number: '', email: '' });
    } catch (err) {
      toast.error(getApiError(err, 'Failed to save address'));
    }
  };

  const handleDeleteAddress = async (addressId) => {
    try {
      await axios.delete(`${API_URL}/api/addresses/${addressId}`);
      setSavedAddresses(prev => prev.filter(a => a.address_id !== addressId));
      if (selectedAddressId === addressId) setSelectedAddressId('');
      toast.success('Address removed');
    } catch {
      toast.error('Failed to delete address');
    }
  };

  const today = new Date().toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' });

  return (
    <CustomerLayout>
      <div className="animate-fade-in" data-testid="create-waybill-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center text-sm text-slate-500 mb-2">
              <span>Operations</span>
              <span className="mx-2">›</span>
              <span className="text-teal-700 font-medium">New Waybill</span>
            </div>
            <h1 className="page-title">Create Waybill</h1>
          </div>
          <div className="flex items-center space-x-4">
            <div className="bg-slate-100 px-4 py-2 rounded-lg">
              <p className="text-xs text-slate-500 uppercase tracking-wide">Waybill Number</p>
              <p className="text-lg font-bold text-teal-700">{reference}</p>
            </div>
            <div className="bg-slate-100 px-4 py-2 rounded-lg">
              <p className="text-xs text-slate-500 uppercase tracking-wide">Issue Date</p>
              <p className="text-lg font-semibold text-slate-900">{today}</p>
            </div>
          </div>
        </div>

        <form onSubmit={(e) => e.preventDefault()}>
          {/* Sender & Receiver Cards */}
          <div className="grid lg:grid-cols-2 gap-6 mb-6">
            {/* Sender Details */}
            <Card className="card-elevated" data-testid="sender-details-card">
              <CardHeader className="pb-4">
                <div className="flex items-center space-x-3">
                  <div className="h-10 w-10 rounded-lg bg-teal-50 flex items-center justify-center">
                    <MapPin className="h-5 w-5 text-teal-600" />
                  </div>
                  <CardTitle className="card-title">Sender Details</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="label-text">Company Name</Label>
                  <Input
                    name="sender_company"
                    value={formData.sender_company}
                    onChange={handleChange}
                    placeholder="Global Logistics Hub"
                    className="input-field"
                    required
                    data-testid="sender-company-input"
                  />
                </div>
                <div>
                  <Label className="label-text">Address</Label>
                  <Textarea
                    name="sender_address"
                    value={formData.sender_address}
                    onChange={handleChange}
                    placeholder="12 Trade Center Way, Midrand, Johannesburg, 1685"
                    className="input-field min-h-[80px]"
                    required
                    data-testid="sender-address-input"
                  />
                </div>
                <div>
                  <Label className="label-text">Origin City</Label>
                  <Input
                    name="origin_city"
                    value={formData.origin_city}
                    onChange={handleChange}
                    placeholder="e.g. Johannesburg"
                    className="input-field"
                    required
                    data-testid="origin-city-input"
                  />
                </div>
                <div>
                  <Label className="label-text">Contact Person</Label>
                  <Input
                    name="sender_contact_person"
                    value={formData.sender_contact_person}
                    onChange={handleChange}
                    placeholder="Enter contact name"
                    className="input-field"
                    required
                    data-testid="sender-contact-input"
                  />
                </div>
                <div>
                  <Label className="label-text">Contact Number</Label>
                  <Input
                    name="sender_contact_number"
                    value={formData.sender_contact_number}
                    onChange={handleChange}
                    placeholder="Enter contact number"
                    className="input-field"
                    required
                    data-testid="sender-phone-input"
                  />
                </div>
                <div>
                  <Label className="label-text">Email</Label>
                  <Input
                    name="sender_email"
                    type="email"
                    value={formData.sender_email}
                    onChange={handleChange}
                    placeholder="sender@company.com"
                    className="input-field"
                    required
                    data-testid="sender-email-input"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Receiver Details */}
            <Card className="card-elevated" data-testid="receiver-details-card">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="h-10 w-10 rounded-lg bg-purple-50 flex items-center justify-center">
                      <Send className="h-5 w-5 text-purple-600" />
                    </div>
                    <CardTitle className="card-title">Receiver Details</CardTitle>
                  </div>
                  <button
                    type="button"
                    onClick={() => openAddressModal()}
                    className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm text-slate-400 hover:text-teal-600 hover:bg-teal-50 transition-colors"
                    title="Manage saved addresses"
                    data-testid="manage-addresses-btn"
                  >
                    <Settings className="h-4 w-4" />
                    <span className="text-xs font-medium">Manage Saved Addresses</span>
                  </button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Address selector dropdown */}
                {savedAddresses.length > 0 && (
                  <div data-testid="saved-address-selector">
                    <Label className="label-text">Load Saved Address</Label>
                    <Select
                      value={selectedAddressId}
                      onValueChange={handleSelectAddress}
                    >
                      <SelectTrigger className="input-field h-11" data-testid="address-select">
                        <SelectValue placeholder="Type new or select saved address" />
                      </SelectTrigger>
                      <SelectContent>
                        {savedAddresses.map(addr => (
                          <SelectItem key={addr.address_id} value={addr.address_id}>
                            {addr.label || addr.company_name}{addr.city ? ` — ${addr.city}` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div>
                  <Label className="label-text">Recipient Name / Company</Label>
                  <Input
                    name="receiver_company"
                    value={formData.receiver_company}
                    onChange={handleChange}
                    placeholder="Enter recipient details"
                    className="input-field"
                    required
                    data-testid="receiver-company-input"
                  />
                </div>
                <div>
                  <Label className="label-text">Destination Address</Label>
                  <Textarea
                    name="receiver_address"
                    value={formData.receiver_address}
                    onChange={handleChange}
                    placeholder="Enter full delivery address"
                    className="input-field min-h-[80px]"
                    required
                    data-testid="receiver-address-input"
                  />
                </div>
                <div>
                  <Label className="label-text">Destination City</Label>
                  <Input
                    name="destination_city"
                    value={formData.destination_city}
                    onChange={handleChange}
                    placeholder="e.g. Cape Town"
                    className="input-field"
                    required
                    data-testid="destination-city-input"
                  />
                </div>
                <div>
                  <Label className="label-text">Contact Person</Label>
                  <Input
                    name="receiver_contact_person"
                    value={formData.receiver_contact_person}
                    onChange={handleChange}
                    placeholder="Enter contact name"
                    className="input-field"
                    required
                    data-testid="receiver-contact-input"
                  />
                </div>
                <div>
                  <Label className="label-text">Contact Number</Label>
                  <Input
                    name="receiver_contact_number"
                    value={formData.receiver_contact_number}
                    onChange={handleChange}
                    placeholder="Enter contact number"
                    className="input-field"
                    required
                    data-testid="receiver-phone-input"
                  />
                </div>
                <div>
                  <Label className="label-text">Email</Label>
                  <Input
                    name="receiver_email"
                    type="email"
                    value={formData.receiver_email}
                    onChange={handleChange}
                    placeholder="receiver@company.com"
                    className="input-field"
                    required
                    data-testid="receiver-email-input"
                  />
                </div>
                {/* Save address for future use */}
                {!showSaveInline ? (
                  <button
                    type="button"
                    onClick={() => setShowSaveInline(true)}
                    className="flex items-center gap-2 text-sm text-teal-600 hover:text-teal-700 font-medium pt-1 transition-colors"
                    data-testid="save-address-toggle"
                  >
                    <Plus className="h-4 w-4" />
                    Save address for future use
                  </button>
                ) : (
                  <div className="flex items-center gap-2 pt-1" data-testid="save-address-inline">
                    <Input
                      value={saveLabel}
                      onChange={(e) => setSaveLabel(e.target.value)}
                      placeholder="Address name, e.g. Head Office"
                      className="input-field flex-1"
                      data-testid="save-address-name-input"
                    />
                    <Button
                      type="button"
                      onClick={async () => {
                        if (!saveLabel.trim()) { toast.error('Enter an address name'); return; }
                        try {
                          await axios.post(`${API_URL}/api/addresses`, {
                            label: saveLabel.trim(),
                            company_name: formData.receiver_company,
                            address: formData.receiver_address,
                            city: formData.destination_city,
                            contact_person: formData.receiver_contact_person,
                            contact_number: formData.receiver_contact_number,
                            email: formData.receiver_email,
                          });
                          const refreshed = await axios.get(`${API_URL}/api/addresses`);
                          setSavedAddresses(refreshed.data.addresses || []);
                          toast.success('Address saved');
                          setShowSaveInline(false);
                          setSaveLabel('');
                        } catch (err) {
                          toast.error(getApiError(err, 'Failed to save'));
                        }
                      }}
                      className="bg-teal-600 hover:bg-teal-700 text-white h-10 px-4"
                      data-testid="save-address-confirm-btn"
                    >
                      Save
                    </Button>
                    <button
                      type="button"
                      onClick={() => { setShowSaveInline(false); setSaveLabel(''); }}
                      className="text-slate-400 hover:text-slate-600 h-10 px-2"
                      data-testid="save-address-cancel-btn"
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Manage Addresses Modal */}
            <Dialog open={addressModalOpen} onOpenChange={setAddressModalOpen}>
              <DialogContent className="max-w-lg" data-testid="address-modal">
                <DialogHeader>
                  <DialogTitle>
                    {showAddressForm ? (editingAddress ? 'Edit Address' : 'Add New Address') : 'Saved Addresses'}
                  </DialogTitle>
                </DialogHeader>

                {!showAddressForm ? (
                  /* List View */
                  <div className="space-y-3">
                    <Button
                      type="button"
                      onClick={() => { setEditingAddress(null); setAddressForm({ label: '', company_name: '', address: '', city: '', contact_person: '', contact_number: '', email: '' }); setShowAddressForm(true); }}
                      className="w-full bg-teal-600 hover:bg-teal-700 text-white"
                      data-testid="add-new-address-btn"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add New Address
                    </Button>
                    {savedAddresses.length > 0 ? (
                      <div className="space-y-2 max-h-72 overflow-y-auto">
                        {savedAddresses.map(addr => (
                          <div key={addr.address_id} className="flex items-center justify-between p-3 rounded-lg border border-slate-200 bg-slate-50">
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-sm text-slate-900">{addr.label || addr.company_name}</p>
                              <p className="text-xs text-slate-600 mt-0.5">{addr.company_name}{addr.city ? `, ${addr.city}` : ''}</p>
                              <p className="text-xs text-slate-400 truncate">{addr.address}</p>
                            </div>
                            <div className="flex items-center gap-1 ml-3 flex-shrink-0">
                              <button
                                type="button"
                                onClick={() => openAddressModal(addr)}
                                className="h-8 w-8 rounded flex items-center justify-center text-slate-400 hover:text-teal-600 hover:bg-teal-50 transition-colors"
                                data-testid={`edit-addr-${addr.address_id}`}
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeleteAddress(addr.address_id)}
                                className="h-8 w-8 rounded flex items-center justify-center text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                                data-testid={`delete-addr-${addr.address_id}`}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-center text-sm text-slate-400 py-6">No saved addresses yet</p>
                    )}
                  </div>
                ) : (
                  /* Add / Edit Form */
                  <div className="space-y-3">
                    <div>
                      <Label className="text-xs font-semibold text-slate-500">Address Name</Label>
                      <Input value={addressForm.label} onChange={e => setAddressForm(p => ({...p, label: e.target.value}))} placeholder="e.g. Head Office, Cape Town Warehouse" data-testid="addr-form-label" />
                    </div>
                    <div>
                      <Label className="text-xs font-semibold text-slate-500">Company / Recipient</Label>
                      <Input value={addressForm.company_name} onChange={e => setAddressForm(p => ({...p, company_name: e.target.value}))} placeholder="Recipient name" data-testid="addr-form-company" />
                    </div>
                    <div>
                      <Label className="text-xs font-semibold text-slate-500">Address</Label>
                      <Textarea value={addressForm.address} onChange={e => setAddressForm(p => ({...p, address: e.target.value}))} placeholder="Full address" className="min-h-[60px]" data-testid="addr-form-address" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs font-semibold text-slate-500">City</Label>
                        <Input value={addressForm.city} onChange={e => setAddressForm(p => ({...p, city: e.target.value}))} placeholder="City" data-testid="addr-form-city" />
                      </div>
                      <div>
                        <Label className="text-xs font-semibold text-slate-500">Contact Person</Label>
                        <Input value={addressForm.contact_person} onChange={e => setAddressForm(p => ({...p, contact_person: e.target.value}))} placeholder="Contact name" data-testid="addr-form-contact" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs font-semibold text-slate-500">Phone</Label>
                        <Input value={addressForm.contact_number} onChange={e => setAddressForm(p => ({...p, contact_number: e.target.value}))} placeholder="Phone number" data-testid="addr-form-phone" />
                      </div>
                      <div>
                        <Label className="text-xs font-semibold text-slate-500">Email</Label>
                        <Input value={addressForm.email} onChange={e => setAddressForm(p => ({...p, email: e.target.value}))} placeholder="Email" data-testid="addr-form-email" />
                      </div>
                    </div>
                    <div className="flex gap-2 pt-2">
                      <Button type="button" variant="outline" onClick={() => { setShowAddressForm(false); setEditingAddress(null); }} className="flex-1" data-testid="addr-form-back">
                        Back
                      </Button>
                      <Button type="button" onClick={handleSaveAddress} className="flex-1 bg-teal-600 hover:bg-teal-700 text-white" data-testid="addr-form-save">
                        {editingAddress ? 'Update Address' : 'Save Address'}
                      </Button>
                    </div>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </div>

          {/* Delivery Type */}
          <Card className="card-elevated mb-6" data-testid="delivery-type-card">
            <CardHeader className="pb-4">
              <div className="flex items-center space-x-3">
                <div className="h-10 w-10 rounded-lg bg-indigo-50 flex items-center justify-center">
                  <Clock className="h-5 w-5 text-indigo-600" />
                </div>
                <CardTitle className="card-title">Delivery Type</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-3 gap-4 items-start">
                <div>
                  <Label className="label-text">Service Type</Label>
                  <Select
                    value={selectedDeliveryType?.code || ''}
                    onValueChange={(code) => {
                      const dt = deliveryTypes.find(d => d.code === code);
                      setSelectedDeliveryType(dt || null);
                    }}
                  >
                    <SelectTrigger className="input-field h-11" data-testid="delivery-type-select">
                      <SelectValue placeholder="Select delivery type" />
                    </SelectTrigger>
                    <SelectContent>
                      {deliveryTypes.map(dt => (
                        <SelectItem key={dt.code} value={dt.code}>{dt.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {selectedDeliveryType && (
                  <div>
                    <Label className="label-text">Lead Time</Label>
                    <div className="flex items-center h-11 px-4 rounded-lg bg-indigo-50 border border-indigo-100">
                      <Clock className="h-4 w-4 text-indigo-600 mr-2 flex-shrink-0" />
                      <span className="text-sm font-medium text-indigo-700">{selectedDeliveryType.sla}</span>
                    </div>
                  </div>
                )}
                {selectedDeliveryType && getExpectedDeliveryDate(selectedDeliveryType) && (
                  <div>
                    <Label className="label-text">Expected Delivery</Label>
                    <div className="flex items-center h-11 px-4 rounded-lg bg-emerald-50 border border-emerald-100" data-testid="expected-delivery-date">
                      <svg className="h-4 w-4 text-emerald-600 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span className="text-sm font-medium text-emerald-700">{formatDate(getExpectedDeliveryDate(selectedDeliveryType))}</span>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Item Details */}
          <Card className="card-elevated mb-6" data-testid="item-details-card">
            <CardHeader className="pb-4 flex flex-row items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="h-10 w-10 rounded-lg bg-blue-50 flex items-center justify-center">
                  <Package className="h-5 w-5 text-blue-600" />
                </div>
                <CardTitle className="card-title">Item Details</CardTitle>
              </div>
              <Button type="button" onClick={addItem} className="btn-primary" data-testid="add-item-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Item
              </Button>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-100">
                      <th className="text-left py-4 px-2 text-xs font-semibold uppercase tracking-wider text-slate-500 w-[12%]">Type</th>
                      <th className="text-left py-4 px-2 text-xs font-semibold uppercase tracking-wider text-slate-500 w-[12%]">Reference</th>
                      <th className="text-left py-4 px-2 text-xs font-semibold uppercase tracking-wider text-slate-500">Description</th>
                      <th className="text-left py-4 px-2 text-xs font-semibold uppercase tracking-wider text-slate-500 w-[6%]">Qty</th>
                      <th className="text-left py-4 px-2 text-xs font-semibold uppercase tracking-wider text-slate-500 w-[10%]">Weight/item (kg)</th>
                      <th className="text-left py-4 px-2 text-xs font-semibold uppercase tracking-wider text-slate-500 w-[20%]">Dimensions (L×W×H cm)</th>
                      <th className="py-4 px-2 w-[4%]"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, index) => (
                      <tr key={item._key} className="border-b border-slate-50">
                        <td className="py-4 px-3">
                          <Select 
                            value={item.package_type} 
                            onValueChange={(v) => handleItemChange(index, 'package_type', v)}
                          >
                            <SelectTrigger className="bg-slate-50 h-11" data-testid={`item-type-${index}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {packageTypes.map(pt => (
                                <SelectItem key={pt.value} value={pt.value}>{pt.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="py-4 px-3">
                          <Input
                            value={item.item_reference}
                            onChange={(e) => handleItemChange(index, 'item_reference', e.target.value)}
                            placeholder="e.g. PO-12345"
                            className="bg-slate-50 h-11"
                            data-testid={`item-reference-${index}`}
                          />
                        </td>
                        <td className="py-4 px-3">
                          <Input
                            value={item.description}
                            onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                            placeholder="e.g. Industrial Pumps"
                            className="bg-slate-50 h-11"
                            data-testid={`item-description-${index}`}
                          />
                        </td>
                        <td className="py-4 px-3">
                          <Input
                            type="number"
                            min="1"
                            value={item.quantity}
                            onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                            className="bg-slate-50 h-11"
                            data-testid={`item-qty-${index}`}
                          />
                        </td>
                        <td className="py-4 px-3">
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={item.weight}
                            onChange={(e) => handleItemChange(index, 'weight', e.target.value)}
                            placeholder="0.00"
                            className="bg-slate-50 h-11"
                            data-testid={`item-weight-${index}`}
                          />
                        </td>
                        <td className="py-4 px-3">
                          <div className="flex items-center space-x-2">
                            <Input
                              type="number"
                              min="0"
                              value={item.length}
                              onChange={(e) => handleItemChange(index, 'length', e.target.value)}
                              placeholder="L"
                              className="bg-slate-50 h-11"
                            />
                            <span className="text-slate-400 text-lg">×</span>
                            <Input
                              type="number"
                              min="0"
                              value={item.width}
                              onChange={(e) => handleItemChange(index, 'width', e.target.value)}
                              placeholder="W"
                              className="bg-slate-50 h-11"
                            />
                            <span className="text-slate-400 text-lg">×</span>
                            <Input
                              type="number"
                              min="0"
                              value={item.height}
                              onChange={(e) => handleItemChange(index, 'height', e.target.value)}
                              placeholder="H"
                              className="bg-slate-50 h-11"
                            />
                          </div>
                        </td>
                        <td className="py-4 px-3">
                          <Button
                            type="button"
                            variant="ghost"
                            onClick={() => removeItem(index)}
                            disabled={items.length === 1}
                            className="text-slate-400 hover:text-red-500"
                            data-testid={`remove-item-${index}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Notes Section */}
          <Card className="card-elevated mb-6" data-testid="notes-card">
            <CardHeader className="pb-4">
              <div className="flex items-center space-x-3">
                <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center">
                  <FileText className="h-5 w-5 text-slate-600" />
                </div>
                <div>
                  <CardTitle className="card-title">Additional Notes</CardTitle>
                  <p className="text-sm text-slate-500 mt-0.5">Any special instructions, delivery requirements, or additional information.</p>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Textarea
                name="customer_notes"
                value={formData.customer_notes}
                onChange={handleChange}
                placeholder="e.g. Deliver before 10am, call receiver 30 min before arrival, fragile items require careful handling..."
                className="input-field min-h-[120px]"
                data-testid="customer-notes-input"
              />
            </CardContent>
          </Card>

          {/* Submit Options */}
          <div className="grid sm:grid-cols-2 gap-4 mb-6">
            <Card className="bg-amber-50/50 border-amber-200 hover:border-amber-300 transition-colors cursor-pointer group" data-testid="option-request-quote">
              <CardContent className="p-6">
                <div className="flex items-start space-x-3 mb-4">
                  <div className="h-10 w-10 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                    <FileText className="h-5 w-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Request a Quote</p>
                    <p className="text-sm text-slate-600 mt-1">
                      Our team will calculate freight costs and send you a quote to review before proceeding.
                    </p>
                  </div>
                </div>
                <Button 
                  onClick={() => handleSubmit('quote')} 
                  disabled={loading} 
                  className="w-full bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="submit-quote-btn"
                >
                  <FileText className="h-4 w-4 mr-2" />
                  {loading ? 'Submitting...' : 'Request Quote'}
                </Button>
              </CardContent>
            </Card>

            <Card className="bg-teal-50/50 border-teal-200 hover:border-teal-300 transition-colors cursor-pointer group" data-testid="option-send-now">
              <CardContent className="p-6">
                <div className="flex items-start space-x-3 mb-4">
                  <div className="h-10 w-10 rounded-lg bg-teal-100 flex items-center justify-center flex-shrink-0">
                    <Send className="h-5 w-5 text-teal-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Ship Now</p>
                    <p className="text-sm text-slate-600 mt-1">
                      Skip the quote and confirm this job immediately. Our team will process it straight away.
                    </p>
                  </div>
                </div>
                <Button 
                  onClick={() => handleSubmit('direct')} 
                  disabled={loading} 
                  className="w-full btn-primary"
                  data-testid="submit-direct-btn"
                >
                  <Send className="h-4 w-4 mr-2" />
                  {loading ? 'Submitting...' : 'Ship Now'}
                </Button>
              </CardContent>
            </Card>
          </div>

        </form>
      </div>
    </CustomerLayout>
  );
};

export default CreateWaybill;
